/// <reference types="vite/client" />
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { initSentry, sentryErrorHandler } from '@/modules/sentry';
import { initConsoleOverride } from '@/shared/utils/console-override';
import { Toaster } from '@kognitos/lattice';
import '@/shared/i18n';

import { CONNECTIVITY_TOASTER_ID } from '@/shared/connectivity';
import router from './routes';
import { Providers } from './providers';
import './index.css';

// Reload the page when a dynamic import fails to load a chunk.
// After a new deployment the old chunk hashes no longer exist; any user
// whose browser cached the previous index.html will hit 404s on those URLs.
// Reloading fetches the latest index.html and resolves the mismatch.
// The sessionStorage guard ensures at most one auto-reload per session so a
// bad deployment or offline state cannot cause an infinite reload loop.
const VITE_PRELOAD_RELOAD_KEY = 'vite_preload_reloaded';
// eslint-disable-next-line no-restricted-syntax -- reason: Vite's preloadError fires at module load before React mounts; React callbacks and Zustand are unavailable here
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem(VITE_PRELOAD_RELOAD_KEY)) {
    return; // Already reloaded once this session — don't loop
  }
  sessionStorage.setItem(VITE_PRELOAD_RELOAD_KEY, '1');
  window.location.reload();
});

// Initialize Sentry first so its breadcrumb hooks are in place,
// then silence console output in non-development environments.
initSentry();
initConsoleOverride();

const root = createRoot(document.getElementById('root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: sentryErrorHandler,
});
root.render(
  <StrictMode>
    <Providers>
      <RouterProvider router={router} />
      <Toaster />
      {/* Connectivity notices get their own toaster (top-center, addressed by
          id). Sonner labels every container "Notifications alt+T" by default,
          so without an explicit label the two mounts present as duplicate,
          indistinguishable landmarks to a screen reader. */}
      <Toaster
        id={CONNECTIVITY_TOASTER_ID}
        position="top-center"
        containerAriaLabel="Connection notifications"
      />
    </Providers>
  </StrictMode>,
);
